/**
 * Device detection utilities for AR measurements
 * Detects device model from User-Agent for camera calibration
 */

export interface DeviceInfo {
  manufacturer: string;
  model: string;
  modelIdentifier: string;
  userAgent: string;
  platform: string;
}

/**
 * Detect device information from User-Agent
 */
export function detectDevice(): DeviceInfo {
  const ua = navigator.userAgent;
  const platform = navigator.platform;

  // iOS detection - Try to extract model identifier like "iPhone15,2"
  const iosMatch = ua.match(/iPhone(\d+,\d+)/);
  if (iosMatch) {
    const modelId = iosMatch[0]; // e.g., "iPhone15,2"

    // Map known model identifiers to device names
    const modelMap: Record<string, string> = {
      'iPhone16,2': 'iPhone 15 Pro Max',
      'iPhone16,1': 'iPhone 15 Pro',
      'iPhone15,5': 'iPhone 15 Plus',
      'iPhone15,4': 'iPhone 15',
      'iPhone15,3': 'iPhone 14 Pro Max',
      'iPhone15,2': 'iPhone 14 Pro',
      'iPhone14,8': 'iPhone 14 Plus',
      'iPhone14,7': 'iPhone 14',
      'iPhone14,3': 'iPhone 13 Pro Max',
      'iPhone14,2': 'iPhone 13 Pro',
      'iPhone14,5': 'iPhone 13',
      'iPhone14,4': 'iPhone 13 mini',
    };

    return {
      manufacturer: 'Apple',
      model: modelMap[modelId] || 'iPhone',
      modelIdentifier: modelId,
      userAgent: ua,
      platform: 'iOS'
    };
  }

  // Check for specific iPhone models in user agent
  if (ua.includes('iPhone')) {
    // Try to extract model from user agent string
    if (ua.includes('iPhone 15 Pro Max')) {
      return {
        manufacturer: 'Apple',
        model: 'iPhone 15 Pro Max',
        modelIdentifier: 'iPhone16,2',
        userAgent: ua,
        platform: 'iOS'
      };
    }
    if (ua.includes('iPhone 15 Pro')) {
      return {
        manufacturer: 'Apple',
        model: 'iPhone 15 Pro',
        modelIdentifier: 'iPhone16,1',
        userAgent: ua,
        platform: 'iOS'
      };
    }
    if (ua.includes('iPhone 15')) {
      return {
        manufacturer: 'Apple',
        model: 'iPhone 15',
        modelIdentifier: 'iPhone15,4',
        userAgent: ua,
        platform: 'iOS'
      };
    }
    if (ua.includes('iPhone 14 Pro')) {
      return {
        manufacturer: 'Apple',
        model: 'iPhone 14 Pro',
        modelIdentifier: 'iPhone15,2',
        userAgent: ua,
        platform: 'iOS'
      };
    }
    if (ua.includes('iPhone 14')) {
      return {
        manufacturer: 'Apple',
        model: 'iPhone 14',
        modelIdentifier: 'iPhone14,7',
        userAgent: ua,
        platform: 'iOS'
      };
    }
    if (ua.includes('iPhone 13 Pro')) {
      return {
        manufacturer: 'Apple',
        model: 'iPhone 13 Pro',
        modelIdentifier: 'iPhone14,2',
        userAgent: ua,
        platform: 'iOS'
      };
    }
    if (ua.includes('iPhone 13')) {
      return {
        manufacturer: 'Apple',
        model: 'iPhone 13',
        modelIdentifier: 'iPhone14,5',
        userAgent: ua,
        platform: 'iOS'
      };
    }

    // Generic iPhone fallback
    return {
      manufacturer: 'Apple',
      model: 'iPhone',
      modelIdentifier: 'iPhone',
      userAgent: ua,
      platform: 'iOS'
    };
  }

  // Android detection
  const androidMatch = ua.match(/Android.*; ([^;]+)\)/);
  if (androidMatch) {
    const deviceModel = androidMatch[1].trim();

    // Extract manufacturer and model
    const parts = deviceModel.split(' ');
    const manufacturer = parts[0] || 'Unknown';
    const model = deviceModel;

    return {
      manufacturer,
      model,
      modelIdentifier: deviceModel,
      userAgent: ua,
      platform: 'Android'
    };
  }

  // iPad detection
  if (ua.includes('iPad')) {
    return {
      manufacturer: 'Apple',
      model: 'iPad',
      modelIdentifier: 'iPad',
      userAgent: ua,
      platform: 'iOS'
    };
  }

  // Desktop/Unknown fallback
  return {
    manufacturer: 'Unknown',
    model: 'Unknown',
    modelIdentifier: 'unknown',
    userAgent: ua,
    platform: platform
  };
}

/**
 * Get video track settings from MediaStream
 * Useful for extracting additional device info
 */
export function getVideoTrackSettings(stream: MediaStream): MediaTrackSettings | null {
  const videoTrack = stream.getVideoTracks()[0];
  if (videoTrack) {
    const settings = videoTrack.getSettings();
    return settings;
  }
  return null;
}

/**
 * Format device info for display
 */
export function formatDeviceInfo(deviceInfo: DeviceInfo): string {
  if (deviceInfo.manufacturer === 'Unknown') {
    return 'Unknown Device';
  }
  return `${deviceInfo.manufacturer} ${deviceInfo.model}`;
}

/**
 * Check if device is likely mobile
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Check if device is iOS
 */
export function isIOSDevice(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Check if device is Android
 */
export function isAndroidDevice(): boolean {
  return /Android/i.test(navigator.userAgent);
}
